import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "mapWrapper",
    "roundBadge",
    "activeBanner",
    "activeBannerName",
    "initiativeList",
    "zoomLabel",
  ]
  static values = {
    zoom:            { type: Number,  default: 1 },
    panX:            { type: Number,  default: 0 },
    panY:            { type: Number,  default: 0 },
    encounterActive: { type: Boolean, default: false },
    currentRound:    { type: Number,  default: 0 },
    activeTokenId:   { type: Number,  default: 0 },
  }

  connect() {
    this.element.classList.add("tv-mode")

    // Bind stage_controller event dispatches
    this._onEncounterUpdate = (e) => this.onEncounterUpdate(e.detail)
    this._onTokenUpdate     = (e) => this.onTokenUpdate(e.detail)
    this._onTokenCreate     = (e) => { setTimeout(() => this.buildInitiativeList(), 120) }
    this._onTokenDestroy    = (e) => this.removeInitiativeRow(e.detail.id)
    this.element.addEventListener("stage:encounter-update", this._onEncounterUpdate)
    this.element.addEventListener("stage:token-update",     this._onTokenUpdate)
    this.element.addEventListener("stage:token-create",     this._onTokenCreate)
    this.element.addEventListener("stage:token-destroy",    this._onTokenDestroy)

    // Wheel zoom
    this._onWheel = (e) => this.onWheel(e)
    this.element.addEventListener("wheel", this._onWheel, { passive: false })

    // Pointer pan (drag on background when zoomed)
    this.panState = null
    this._onPanDown  = (e) => this.onPanDown(e)
    this._onPanMove  = (e) => this.onPanMove(e)
    this._onPanUp    = (e) => { this.panState = null }
    this.element.addEventListener("pointerdown", this._onPanDown)
    this.element.addEventListener("pointermove", this._onPanMove)
    this.element.addEventListener("pointerup",   this._onPanUp)

    // Bootstrap initial state from data values on the element
    this.buildInitiativeList()
    this.applyZoom()
    this.updateRoundBadge()
    this.updateActiveBanner(this.activeTokenIdValue)
  }

  disconnect() {
    this.element.classList.remove("tv-mode")
    this.element.removeEventListener("stage:encounter-update", this._onEncounterUpdate)
    this.element.removeEventListener("stage:token-update",     this._onTokenUpdate)
    this.element.removeEventListener("stage:token-create",     this._onTokenCreate)
    this.element.removeEventListener("stage:token-destroy",    this._onTokenDestroy)
    this.element.removeEventListener("wheel",        this._onWheel)
    this.element.removeEventListener("pointerdown",  this._onPanDown)
    this.element.removeEventListener("pointermove",  this._onPanMove)
    this.element.removeEventListener("pointerup",    this._onPanUp)
  }

  // ── Zoom & Pan ──────────────────────────────────────────────────────

  onWheel(e) {
    e.preventDefault()
    const step  = e.deltaY > 0 ? -0.1 : 0.1
    this.zoomValue = Math.max(0.5, Math.min(4, this.zoomValue + step))
    this.applyZoom()
  }

  zoomIn()  { this.zoomValue = Math.min(4,   this.zoomValue + 0.25); this.applyZoom() }
  zoomOut() { this.zoomValue = Math.max(0.5, this.zoomValue - 0.25); this.applyZoom() }

  fitToScreen() {
    this.zoomValue = 1
    this.panXValue = 0
    this.panYValue = 0
    this.applyZoom()
  }

  applyZoom() {
    if (!this.hasMapWrapperTarget) return
    const z = this.zoomValue
    this.mapWrapperTarget.style.transformOrigin = "center center"
    this.mapWrapperTarget.style.transform = `scale(${z}) translate(${this.panXValue / z}px, ${this.panYValue / z}px)`
    if (this.hasZoomLabelTarget) {
      this.zoomLabelTarget.textContent = `${Math.round(z * 100)}%`
    }
  }

  onPanDown(e) {
    if (e.button !== 0) return
    if (e.target.closest("[data-token-id]")) return
    if (e.target.closest(".tv-zoom-controls")) return
    if (e.target.closest(".tv-initiative-strip")) return
    this.panState = {
      startX:    e.clientX,
      startY:    e.clientY,
      startPanX: this.panXValue,
      startPanY: this.panYValue,
    }
    this.element.setPointerCapture(e.pointerId)
  }

  onPanMove(e) {
    if (!this.panState) return
    this.panXValue = this.panState.startPanX + (e.clientX - this.panState.startX)
    this.panYValue = this.panState.startPanY + (e.clientY - this.panState.startY)
    this.applyZoom()
  }

  // ── Encounter update ────────────────────────────────────────────────

  onEncounterUpdate(data) {
    this.encounterActiveValue = data.encounter_active
    this.currentRoundValue    = data.current_round
    this.activeTokenIdValue   = data.active_token_id || 0
    this.updateRoundBadge()
    this.updateActiveBanner(this.activeTokenIdValue)
    this.highlightActiveInList(this.activeTokenIdValue)
  }

  updateRoundBadge() {
    if (!this.hasRoundBadgeTarget) return
    const show = this.encounterActiveValue && this.currentRoundValue > 0
    this.roundBadgeTarget.textContent = `Round ${this.currentRoundValue}`
    this.roundBadgeTarget.classList.toggle("hidden", !show)
  }

  updateActiveBanner(activeId) {
    if (!this.hasActiveBannerTarget) return
    if (!activeId) {
      this.activeBannerTarget.classList.add("hidden")
      return
    }
    const tokenEl = this.element.querySelector(`[data-token-id="${activeId}"]`)
    const label   = tokenEl ? (tokenEl.dataset.tokenLabel || "—") : "—"
    if (this.hasActiveBannerNameTarget) this.activeBannerNameTarget.textContent = label
    this.activeBannerTarget.classList.remove("hidden")
  }

  // ── Token updates ───────────────────────────────────────────────────

  onTokenUpdate(token) {
    if (!token) return
    this.updateInitiativeRow(token)
  }

  removeInitiativeRow(id) {
    const row = this.initiativeListTarget?.querySelector(`[data-initiative-id="${id}"]`)
    if (row) row.remove()
  }

  // ── Initiative strip ────────────────────────────────────────────────

  buildInitiativeList() {
    if (!this.hasInitiativeListTarget) return

    const tokenEls = [...this.element.querySelectorAll("[data-token-id]")]
      .filter(el => !el.classList.contains("stage-token-ghost"))

    const tokens = tokenEls.map(el => ({
      id:        el.dataset.tokenId,
      label:     el.dataset.tokenLabel || "?",
      kind:      el.dataset.tokenKind  || "monster",
      currentHp: parseInt(el.dataset.tokenCurrentHp, 10),
      maxHp:     parseInt(el.dataset.tokenMaxHp, 10),
    })).sort((a, b) => a.label.localeCompare(b.label))

    this.initiativeListTarget.innerHTML = ""
    tokens.forEach(t => this.initiativeListTarget.appendChild(this.buildInitiativeRow(t)))
    this.highlightActiveInList(this.activeTokenIdValue)
  }

  buildInitiativeRow(t) {
    const div   = document.createElement("div")
    div.className = "tv-initiative-row"
    div.dataset.initiativeId = t.id

    const label = document.createElement("span")
    label.className = "tv-initiative-label"
    label.dataset.initiativeLabelFor = t.id
    label.textContent = t.label
    div.appendChild(label)

    const hasHp = !isNaN(t.currentHp) && !isNaN(t.maxHp) && t.maxHp > 0
    if (hasHp) {
      const hpSpan = document.createElement("span")
      hpSpan.className = "tv-initiative-hp"
      hpSpan.dataset.initiativeHpFor = t.id
      hpSpan.textContent = `${t.currentHp} / ${t.maxHp}`
      div.appendChild(hpSpan)

      const wrap = document.createElement("div")
      wrap.className = "tv-initiative-hp-bar-wrap"
      const bar = document.createElement("div")
      bar.className = "tv-initiative-hp-bar"
      bar.dataset.initiativeBarFor = t.id
      const pct = Math.max(0, Math.min(100, (t.currentHp / t.maxHp) * 100))
      bar.style.width = `${pct}%`
      bar.style.background = this.hpColor(pct)
      wrap.appendChild(bar)
      div.appendChild(wrap)
    }

    return div
  }

  updateInitiativeRow(token) {
    const row = this.initiativeListTarget?.querySelector(`[data-initiative-id="${token.id}"]`)
    if (!row) return

    const hpSpan = row.querySelector(`[data-initiative-hp-for="${token.id}"]`)
    const bar    = row.querySelector(`[data-initiative-bar-for="${token.id}"]`)
    const label  = row.querySelector(`[data-initiative-label-for="${token.id}"]`)

    if (label && token.label) label.textContent = token.label
    if (hpSpan && token.max_hp > 0) {
      hpSpan.textContent = `${token.current_hp} / ${token.max_hp}`
      const pct = Math.max(0, Math.min(100, (token.current_hp / token.max_hp) * 100))
      if (bar) { bar.style.width = `${pct}%`; bar.style.background = this.hpColor(pct) }
    }
  }

  highlightActiveInList(activeId) {
    if (!this.hasInitiativeListTarget) return
    this.initiativeListTarget.querySelectorAll(".tv-initiative-row").forEach(row => {
      row.classList.toggle("tv-initiative-active", String(row.dataset.initiativeId) === String(activeId))
    })
  }

  hpColor(pct) {
    if (pct > 60) return "#4a8a5a"
    if (pct > 30) return "#c9a96e"
    return "#b54545"
  }
}
